import { useCallback, useRef } from 'react';

import type { ImpersonationBannerProps } from './ImpersonationBanner.types';

import { Banner } from '@/components/ui';
import { useAuth } from '@/hooks/auth';
import {
  IMPERSONATION_COPY,
  impersonatorOf,
  leaveImpersonation,
} from '@/utils/impersonation';

/**
 * The strip across the top of every signed-in page while an admin is signed
 * in as the member from the admin panel: who is signed in as whom, that it is
 * read-only, and Leave. Renders nothing otherwise.
 *
 * Warning, not info: the state has consequences (nothing saves, and the
 * window closes on its own). No dismiss, and no countdown — the API doesn't
 * send the expiry. `role="status"`, not the primitive's `alert`: it is
 * standing state, read once, not an interruption.
 *
 * `onHeightChange` is for a shell whose header and sidebar are fixed: the
 * strip wraps to two lines on a phone, so they are offset by what it measures.
 */
export function ImpersonationBanner({
  onHeightChange,
  className = '',
}: ImpersonationBannerProps): React.JSX.Element | null {
  const { user } = useAuth();
  const impersonator = impersonatorOf(user);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measure = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!onHeightChange) return;
      if (!node) {
        onHeightChange(0);
        return;
      }

      onHeightChange(Math.round(node.offsetHeight));

      // jsdom has no ResizeObserver; the mount measurement above still lands.
      if (typeof ResizeObserver !== 'function') return;

      const observer = new ResizeObserver(() => {
        onHeightChange(Math.round(node.offsetHeight));
      });
      observer.observe(node);
      observerRef.current = observer;
    },
    [onHeightChange]
  );

  if (!impersonator || !user) return null;

  return (
    <div ref={measure} className={className}>
      <Banner
        variant="warning"
        role="status"
        action={{
          label: IMPERSONATION_COPY.leave,
          onClick: () => void leaveImpersonation(),
        }}
      >
        <span className="font-semibold">{impersonator}</span>{' '}
        {IMPERSONATION_COPY.signedInAs}{' '}
        <span className="font-semibold">{user.username}</span>{' '}
        {IMPERSONATION_COPY.fromPanel}
      </Banner>
    </div>
  );
}

export default ImpersonationBanner;
